// Business days (2026-10-13): the Tasks form's "5th Business Day" box. Pure,
// on calendar dates ('YYYY-MM-DD'), so the form and the server agree and no
// time zone ever shifts a day. "Today" is the date in America/New_York
// (easternDate() in ./tasks.js).
//
// A business day is a weekday that is not a US federal holiday. The eleven
// federal holidays, with the observed rule for the fixed-date ones (on a
// Saturday, observed the Friday before; on a Sunday, the Monday after):
//   New Year's Day     Jan 1            Juneteenth        Jun 19
//   MLK Day            3rd Mon Jan      Independence Day  Jul 4
//   Presidents' Day    3rd Mon Feb      Labor Day         1st Mon Sep
//   Memorial Day       last Mon May     Columbus Day      2nd Mon Oct
//   Veterans Day       Nov 11           Thanksgiving      4th Thu Nov
//   Christmas          Dec 25
// New Year's Day on a Saturday is observed on Dec 31 of the year before.

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function toUtc(date) {
  const m = DATE_RE.exec(String(date ?? ''))
  if (!m) throw new Error(`business-days: expected a YYYY-MM-DD date, got '${date}'`)
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}
function fmt(d) {
  return d.toISOString().slice(0, 10)
}
function ymd(year, month, day) {
  return fmt(new Date(Date.UTC(year, month - 1, day)))
}
// The nth (1-based) given weekday (0 = Sunday) of a month.
function nthWeekday(year, month, weekday, n) {
  const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
  return ymd(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7)
}
function lastWeekday(year, month, weekday) {
  const lastDay = new Date(Date.UTC(year, month, 0))
  const back = (lastDay.getUTCDay() - weekday + 7) % 7
  return ymd(year, month, lastDay.getUTCDate() - back)
}
// A fixed-date holiday's observed day.
function observed(year, month, day) {
  const d = new Date(Date.UTC(year, month - 1, day))
  const dow = d.getUTCDay()
  if (dow === 6) d.setUTCDate(d.getUTCDate() - 1)
  if (dow === 0) d.setUTCDate(d.getUTCDate() + 1)
  return fmt(d)
}

// The observed federal holidays falling in a calendar year, as YYYY-MM-DD.
export function federalHolidays(year) {
  const days = [
    observed(year, 1, 1),
    nthWeekday(year, 1, 1, 3),
    nthWeekday(year, 2, 1, 3),
    lastWeekday(year, 5, 1),
    observed(year, 6, 19),
    observed(year, 7, 4),
    nthWeekday(year, 9, 1, 1),
    nthWeekday(year, 10, 1, 2),
    observed(year, 11, 11),
    nthWeekday(year, 11, 4, 4),
    observed(year, 12, 25),
    // Next year's New Year's Day, when a Saturday puts it on Dec 31.
    observed(year + 1, 1, 1),
  ]
  return days.filter((d) => d.startsWith(`${year}-`))
}

const holidayCache = new Map()
export function isFederalHoliday(date) {
  const year = toUtc(date).getUTCFullYear()
  if (!holidayCache.has(year)) holidayCache.set(year, new Set(federalHolidays(year)))
  return holidayCache.get(year).has(fmt(toUtc(date)))
}

export function isBusinessDay(date) {
  const dow = toUtc(date).getUTCDay()
  return dow !== 0 && dow !== 6 && !isFederalHoliday(date)
}

// The nth business day after `date` (which is itself never counted).
export function addBusinessDays(date, n) {
  const d = toUtc(date)
  let left = n
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1)
    if (isBusinessDay(fmt(d))) left--
  }
  return fmt(d)
}

// The "5th Business Day" box's date: five business days after today in
// America/New_York. today: the Eastern date (easternDate()).
export const TASK_BUSINESS_DAYS_AHEAD = 5
export function fifthBusinessDay(today) {
  return addBusinessDays(today, TASK_BUSINESS_DAYS_AHEAD)
}
